// ReelHunt Plus and the club plan, through RevenueCat (which talks to Google Play and the App Store).
// The rest of the app only uses this module: hasPlus(), state(), onBillingChange(), plans(), purchase(), restore(),
// syncServer(), manageUrl() and appUserId().
//
// Two entitlements: "plus" (one person's extras) and "club" (the stand board for one group, bought by one member;
// the server ties it to the group so every member sees it, see server/billing.ts and groups/club.ts).
//
// Modes:
//   store  the Android/iPhone app with real store keys (REVENUECAT_ANDROID_KEY / REVENUECAT_IOS_KEY)
//   test   RevenueCat Test Store on a phone (REVENUECAT_TEST_KEY, only in the billing test build): the real SDK, no money moves
//   sim    localhost (npm run dev): a simulated store kept in localStorage (?sub=… in the address picks a case)
//   web    the website once billing is live (BILLING_ON_WEB): Plus bought in the app shows here for signed-in users
//   off    everything unlocked: an app build without the billing plugin or keys, and the website before billing is live
import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";
import type { TextKey } from "../shared/strings.ts";
import { BILLING_ON_WEB, CLUB_OFFERING, FOUNDING_OFFERING, REVENUECAT_ANDROID_KEY, REVENUECAT_IOS_KEY, REVENUECAT_TEST_KEY } from "./config.ts";
import { LOCALE } from "./i18n.ts";
import { isNative } from "./native.ts";
import { lsJson, lsSet } from "./util.ts";

export type Entitlement = "plus" | "club";
export type Mode = "store" | "test" | "sim" | "web" | "off";

const platform = Capacitor.getPlatform();
const storeKey = platform === "ios" ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
/** The installed app has the billing plugin built in (the app's code comes from the website, so it can't tell otherwise). */
const hasPlugin = () => /\bReelHuntApp\b.*\brc\b/.test(navigator.userAgent);
/** The billing test build (a debug build, npm run android -- --billing-test): RevenueCat closes a release build that
 *  uses a Test Store key, so only this build may use it. */
const isTestBuild = () => /\bReelHuntApp\b.*\brctest\b/.test(navigator.userAgent);
const isLocal = () => /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
export const mode: Mode = isNative()
  ? (!hasPlugin() ? "off" : REVENUECAT_TEST_KEY && isTestBuild() ? "test" : storeKey ? "store" : "off")
  : isLocal() ? "sim" : BILLING_ON_WEB ? "web" : "off";
/** Purchases can be made here (the store apps, the Test Store and the simulator; not the website). */
export const canBuy = (): boolean => mode === "store" || mode === "test" || mode === "sim";

/** One entitlement. kind: none (never had it), trial, active, expired (had it, it ended). */
export interface EntState {
  kind: "none" | "trial" | "active" | "expired";
  expiresAt: number | null;
  willRenew: boolean;
  billingIssue: boolean;
  plan: "month" | "year";
  founding: boolean;
  product: string;
}
export interface BillingState {
  ready: boolean;
  mode: Mode;
  plus: EntState;
  club: EntState;
  managementURL: string | null;
  /** The store couldn't be reached at start and nothing was cached: everything stays unlocked. */
  offline?: boolean;
}
const NONE: EntState = { kind: "none", expiresAt: null, willRenew: false, billingIssue: false, plan: "month", founding: false, product: "" };
// the last known state until the store answers, so a reload doesn't flash the free version
const saved = lsJson<{ plus?: EntState; club?: EntState } | null>("billing", null);
let current: BillingState = { ready: false, mode, plus: saved?.plus ?? NONE, club: saved?.club ?? NONE, managementURL: null };
const listeners = new Set<(s: BillingState) => void>();

export const state = (): BillingState => current;
const on = (e: EntState) => e.kind === "trial" || e.kind === "active";
/** Plus features are open: subscribed, in the free month, or billing isn't in use here. */
export const hasPlus = (): boolean => mode === "off" || !!current.offline || on(current.plus);
/** This person has a club plan of their own (which group it covers is on the server). */
export const ownsClub = (): boolean => on(current.club);
export const billingOff = (): boolean => mode === "off" || !!current.offline;
export function onBillingChange(cb: (s: BillingState) => void): () => void { listeners.add(cb); return () => listeners.delete(cb); }
function set(next: Partial<BillingState>): void {
  current = { ...current, ...next, ready: true, mode };
  lsSet("billing", JSON.stringify({ plus: current.plus, club: current.club }));
  for (const cb of listeners) { try { cb(current); } catch (e) { console.warn(e); } }
}

/* ---------- RevenueCat CustomerInfo → our state ---------- */
interface EntInfo { isActive: boolean; willRenew: boolean; periodType: string; expirationDate: string | null; productIdentifier: string; billingIssueDetectedAt: string | null }
interface CustomerInfoLike { entitlements: { all: Record<string, EntInfo | undefined> }; managementURL: string | null }
function entFrom(ent: EntInfo | undefined): EntState {
  if (!ent) return NONE;
  const product = ent.productIdentifier || "";
  return {
    kind: ent.isActive ? (ent.periodType === "TRIAL" ? "trial" : "active") : "expired",
    expiresAt: ent.expirationDate ? Date.parse(ent.expirationDate) : null,
    willRenew: !!ent.willRenew,
    billingIssue: !!ent.billingIssueDetectedAt,
    plan: /year|annual/i.test(product) ? "year" : "month",
    founding: /founding/i.test(product),
    product,
  };
}
const fromInfo = (ci: CustomerInfoLike): Partial<BillingState> =>
  ({ plus: entFrom(ci.entitlements.all.plus), club: entFrom(ci.entitlements.all.club), managementURL: ci.managementURL ?? null });

/* ---------- start ---------- */
/** `accountId`: the signed-in ReelHunt account, so purchases follow the account to other phones and the website. */
export async function billingStart(accountId: string | null): Promise<void> {
  if (mode === "off") { set({}); return; }
  if (mode === "sim") { set(fromInfo(sim.customerInfo())); return; }
  // the website: the last known state until account.ts brings the server's copy (setWebStatus)
  if (mode === "web") { set({}); return; }
  try {
    await Purchases.configure({ apiKey: mode === "test" ? REVENUECAT_TEST_KEY : storeKey, appUserID: accountId || null });
    await Purchases.addCustomerInfoUpdateListener((ci) => set(fromInfo(ci as unknown as CustomerInfoLike)));
    const { customerInfo } = await Purchases.getCustomerInfo();
    set(fromInfo(customerInfo as unknown as CustomerInfoLike));
  } catch (e) {
    // no connection and nothing cached, or an app build without the plugin: never lock anyone out by mistake
    console.warn("billing start failed", e);
    set({ offline: true });
  }
  document.addEventListener("visibilitychange", () => { if (!document.hidden) void refreshBilling(); });
}
export async function refreshBilling(): Promise<void> {
  if (mode === "sim") { set(fromInfo(sim.customerInfo())); return; }
  if (mode === "store" || mode === "test") {
    try { const { customerInfo } = await Purchases.getCustomerInfo(); set({ ...fromInfo(customerInfo as unknown as CustomerInfoLike), offline: false }); } catch { /* offline */ }
  }
}
/** The website: the account's subscriptions as the server knows them (bought in the app while signed in). */
export async function setWebStatus(s: { plus?: EntState; club?: EntState } | null): Promise<void> {
  if (mode !== "web") return;
  set({ plus: s?.plus ?? NONE, club: s?.club ?? NONE });
}

/** Signed in: tie purchases to the account. Purchases made before signing in move to it. */
export async function identify(accountId: string): Promise<void> {
  if (!accountId || (mode !== "store" && mode !== "test")) return;
  try {
    const { appUserID } = await Purchases.getAppUserID();
    if (appUserID === accountId) return;
    const { customerInfo } = await Purchases.logIn({ appUserID: accountId });
    set(fromInfo(customerInfo as unknown as CustomerInfoLike));
    void syncServer();
  } catch (e) { console.warn("billing logIn failed", e); }
}

/** The id RevenueCat knows this person by (the account id when signed in, otherwise a random id made on the phone). */
export async function appUserId(): Promise<string> {
  if (mode === "sim") return sim.userId();
  if (mode === "store" || mode === "test") { try { return (await Purchases.getAppUserID()).appUserID; } catch { return ""; } }
  return "";
}

/** Tells the server about this person's subscriptions (it checks with RevenueCat itself). Needed for the club plan,
 *  which the server shows to the whole group. Resolves true when the server has the current state. */
export async function syncServer(): Promise<boolean> {
  const id = await appUserId();
  if (!id) return false;
  const body: Record<string, unknown> = { appUserId: id };
  if (mode === "sim") body.sim = { plus: current.plus, club: current.club }; // only the local dev server accepts this
  try {
    const res = await fetch("/api/billing/sync", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal: AbortSignal.timeout(20000) });
    return res.ok;
  } catch { return false; }
}

/* ---------- products ---------- */
interface ProductLike { identifier: string; price: number; priceString: string; currencyCode: string; introPrice: { price: number } | null }
interface PackageLike { identifier: string; product: ProductLike }
interface OfferingLike { identifier: string; monthly: PackageLike | null; annual: PackageLike | null }
export interface Plan {
  kind: "month" | "year";
  pkg: PackageLike;
  price: string;
  /** "about 2,08 €/month" for the year plan. */
  perMonth: number | null;
  currency: string;
  /** Percent saved against twelve monthly payments. */
  save: number;
  /** The store will give the first month free. */
  trial: boolean;
}
export interface Plans { founding: boolean; monthly: Plan | null; annual: Plan | null }

const freeTrial = (p: ProductLike) => !!(p.introPrice && p.introPrice.price === 0);
async function offerings(): Promise<{ current: OfferingLike | null; all: Record<string, OfferingLike> } | null> {
  if (mode === "sim") return sim.offerings();
  if (mode !== "store" && mode !== "test") return null;
  try { return (await Purchases.getOfferings()) as unknown as { current: OfferingLike | null; all: Record<string, OfferingLike> }; } catch { return null; }
}
/** What can be bought: Plus (monthly and yearly, the founding prices while that offer is on) or the club plan.
 *  null when the store has nothing to sell (offline, or not set up yet). */
export async function plans(what: Entitlement): Promise<Plans | null> {
  const o = await offerings();
  const offering = what === "club" ? o?.all[CLUB_OFFERING] : o?.current;
  if (!offering || (!offering.monthly && !offering.annual)) return null;
  const m = offering.monthly?.product, y = offering.annual?.product;
  let eligible = true;
  // iOS can tell in advance whether this Apple ID already used its free month; Android only offers it to those who can have it
  if (platform === "ios" && (mode === "store" || mode === "test")) {
    try {
      const ids = [m, y].filter((p): p is ProductLike => !!p).map((p) => p.identifier);
      const r = await Purchases.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: ids });
      eligible = Object.values(r).some((e) => (e as { status: number }).status === 2);
    } catch { /* assume eligible: the store shows the real price anyway */ }
  }
  const plan = (kind: "month" | "year", pkg: PackageLike | null): Plan | null => {
    if (!pkg) return null;
    const p = pkg.product;
    return { kind, pkg, price: p.priceString, currency: p.currencyCode, trial: eligible && freeTrial(p),
      perMonth: kind === "year" ? Math.floor(p.price / 12 * 100) / 100 : null,
      save: kind === "year" && m ? Math.max(0, Math.round((1 - p.price / (m.price * 12)) * 100)) : 0 };
  };
  return { founding: offering.identifier === FOUNDING_OFFERING, monthly: plan("month", offering.monthly), annual: plan("year", offering.annual) };
}

/* ---------- buying ---------- */
export type BuyResult = { ok: true } | { cancelled: true } | { error: TextKey };
export async function purchase(what: Entitlement, plan: Plan): Promise<BuyResult> {
  try {
    if (mode === "sim") { await sim.delay(); set(fromInfo(sim.purchase(what, plan))); await syncServer(); return { ok: true }; }
    if (mode !== "store" && mode !== "test") return { error: "payBuyInApp" };
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: plan.pkg as never });
    set(fromInfo(customerInfo as unknown as CustomerInfoLike));
    await syncServer();
    return on(current[what]) ? { ok: true } : { error: "payPending" };
  } catch (e) {
    const err = e as { userCancelled?: boolean; code?: string };
    if (err.userCancelled || err.code === "1") return { cancelled: true };
    if (err.code === "3") return { error: "payNotAllowed" };
    if (err.code === "10" || err.code === "35") return { error: "payOffline" };
    return { error: "payFailed" };
  }
}
/** "Restore purchases": a new phone, or the app was reinstalled. */
export async function restore(): Promise<{ restored: boolean } | { error: TextKey }> {
  try {
    if (mode === "sim") { await sim.delay(); set(fromInfo(sim.restore())); await syncServer(); return { restored: on(current.plus) || on(current.club) }; }
    if (mode !== "store" && mode !== "test") return { restored: on(current.plus) || on(current.club) };
    const { customerInfo } = await Purchases.restorePurchases();
    set(fromInfo(customerInfo as unknown as CustomerInfoLike));
    await syncServer();
    return { restored: on(current.plus) || on(current.club) };
  } catch { return { error: "payRestoreFailed" }; }
}
/** The store's own page for cancelling or changing a subscription. */
export function manageUrl(): string {
  if (current.managementURL) return current.managementURL;
  return platform === "ios" ? "https://apps.apple.com/account/subscriptions" : "https://play.google.com/store/account/subscriptions";
}
export const storeName = (): string => platform === "ios" ? "App Store" : "Google Play";

/* ---------- simulated store (localhost only) ---------- */
// One saved record per entitlement: { plan, founding, trial, expires, willRenew }, plus hidden (for "restore"),
// usedTrial and the offer shown (standard / founding). The address picks a case: ?sub=none|trial|active|expired|lapsed|restore,
// &offer=founding, &club=1 (a club plan of your own). The More tab has the same switches under Plus → Test mode.
interface SimSub { plan: "month" | "year"; founding?: boolean; trial: boolean; expires: number; willRenew: boolean }
interface SimState { plus?: SimSub | null; club?: SimSub | null; hidden?: boolean; usedTrial?: boolean; offer?: "standard" | "founding"; id?: string }
const DAY = 864e5;
export const SIM_CASES = ["none", "trial", "active", "expired", "lapsed", "restore"] as const;
export type SimCase = (typeof SIM_CASES)[number];
const sim = {
  load: (): SimState => lsJson<SimState>("billingSim", {}),
  save: (s: SimState) => lsSet("billingSim", JSON.stringify(s)),
  userId(): string {
    const s = sim.load();
    if (!s.id) { s.id = "$RCAnonymousID:sim" + crypto.randomUUID().replace(/-/g, ""); sim.save(s); }
    return s.id;
  },
  preset(name: SimCase, offer?: string | null): void {
    const now = Date.now(), s = sim.load();
    const plus: Record<SimCase, SimSub | null> = {
      none: null,
      trial: { plan: "year", trial: true, expires: now + 23 * DAY, willRenew: true },
      active: { plan: "year", trial: false, expires: now + 200 * DAY, willRenew: true },
      expired: { plan: "month", trial: true, expires: now - 2 * DAY, willRenew: false },
      lapsed: { plan: "month", trial: false, expires: now - 5 * DAY, willRenew: false },
      restore: { plan: "month", trial: false, expires: now + 20 * DAY, willRenew: true },
    };
    s.plus = plus[name];
    if (s.plus && offer === "founding") s.plus.founding = true;
    s.hidden = name === "restore";
    s.usedTrial = name !== "none";
    s.offer = offer === "founding" ? "founding" : "standard";
    sim.save(s);
  },
  setClub(onOff: boolean): void {
    const s = sim.load();
    s.club = onOff ? { plan: "year", trial: false, expires: Date.now() + 300 * DAY, willRenew: true } : null;
    sim.save(s);
  },
  customerInfo(): CustomerInfoLike {
    const s = sim.load(), all: Record<string, EntInfo> = {};
    for (const ent of ["plus", "club"] as const) {
      const sub = s[ent];
      if (!sub || s.hidden) continue;
      const live = sub.expires > Date.now();
      all[ent] = { isActive: live, willRenew: sub.willRenew && live, periodType: sub.trial ? "TRIAL" : "NORMAL", expirationDate: new Date(sub.expires).toISOString(),
        billingIssueDetectedAt: null, productIdentifier: ent === "club" ? "reelhunt_club_yearly" : `reelhunt_${sub.founding ? "founding" : "plus"}_${sub.plan === "year" ? "yearly" : "monthly"}` };
    }
    return { entitlements: { all }, managementURL: null };
  },
  offerings(): { current: OfferingLike; all: Record<string, OfferingLike> } {
    const s = sim.load(), f = s.offer === "founding" || !!s.plus?.founding, t = !s.usedTrial;
    const prod = (identifier: string, price: number, trial: boolean): ProductLike => ({ identifier, price, currencyCode: "EUR", introPrice: trial ? { price: 0 } : null,
      priceString: new Intl.NumberFormat(LOCALE, { style: "currency", currency: "EUR" }).format(price) });
    const base = "reelhunt_" + (f ? "founding" : "plus");
    const plus: OfferingLike = { identifier: f ? FOUNDING_OFFERING : "standard",
      monthly: { identifier: "$rc_monthly", product: prod(base + "_monthly", f ? 1.99 : 2.99, t) },
      annual: { identifier: "$rc_annual", product: prod(base + "_yearly", f ? 19.99 : 24.99, t) } };
    const club: OfferingLike = { identifier: CLUB_OFFERING, monthly: null, annual: { identifier: "$rc_annual", product: prod("reelhunt_club_yearly", 39.99, false) } };
    return { current: plus, all: { [plus.identifier]: plus, [CLUB_OFFERING]: club } };
  },
  purchase(what: Entitlement, plan: Plan): CustomerInfoLike {
    const s = sim.load();
    if (what === "club") { sim.setClub(true); return sim.customerInfo(); }
    const trial = !s.usedTrial, founding = s.offer === "founding" || !!s.plus?.founding;
    s.plus = { plan: plan.kind, founding, trial, expires: Date.now() + (trial ? 30 : plan.kind === "year" ? 365 : 30) * DAY, willRenew: true };
    s.usedTrial = true; s.hidden = false;
    sim.save(s);
    lsSet("simCase", ""); // a real purchase, not one of the test cases
    return sim.customerInfo();
  },
  restore(): CustomerInfoLike { const s = sim.load(); s.hidden = false; sim.save(s); return sim.customerInfo(); },
  delay: () => new Promise((r) => setTimeout(r, 500)),
};
/** Test mode switches (simulator only). */
export const simOffer = (): "standard" | "founding" => sim.load().offer ?? "standard";
export async function simSet(name: SimCase, offer?: string | null): Promise<void> { sim.preset(name, offer); await refreshBilling(); await syncServer(); }
export async function simClub(onOff: boolean): Promise<void> { sim.setClub(onOff); await refreshBilling(); await syncServer(); }
export const simHasClub = (): boolean => !!sim.load().club;
if (mode === "sim") {
  const q = new URLSearchParams(location.search), c = q.get("sub");
  if (c && (SIM_CASES as readonly string[]).includes(c)) { sim.preset(c as SimCase, q.get("offer")); lsSet("simCase", c); }
  else if (q.get("offer") === "founding") { const s = sim.load(); s.offer = "founding"; sim.save(s); }
  if (q.has("club")) sim.setClub(q.get("club") === "1");
}
